/**
 * AutomationsPage — admin-only catalog of predefined automations + recent runs.
 *
 * Two tabs in a single page:
 *   - Catálogo: cards for each automation with title, description, kind chip,
 *     and a tiny "última ejecución" badge.
 *   - Historial: last N runs from the activity log (action LIKE 'automation.%').
 *
 * No toggles, no editing. v1 by design.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "@/pages/admin/AdminShell";
import { Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import Chip from "@/components/ui-dv/Chip";
import { relativeActivity } from "@/lib/relativeDate";
import {
  Lightning, CheckCircle, WarningCircle, ChatCircleDots, UserPlus, Calendar,
  Robot, Bell as BellIcon, Coin,
} from "@phosphor-icons/react";

const ICONS = {
  lead_followup_task:           UserPlus,
  meeting_done_suggestion:      ChatCircleDots,
  lead_overdue_alert:           BellIcon,
  villa_assignment_payment_plan: Coin,
  calendar_meeting_event:       Calendar,
};

const KIND_LABEL = {
  active:  { label: "Activa",      tone: "emerald" },
  semi:    { label: "Semiautomática", tone: "amber" },
  builtin: { label: "Integrada",    tone: "stone" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AutomationsPage() {
  return <AdminShell><AutomationsView /></AdminShell>;
}

function AutomationsView() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/automations/catalog"),
        api.get("/automations/runs?limit=50"),
      ]);
      setCatalog(c.data);
      setRuns(r.data);
    } catch (e) { setError(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <Section
      title="Automatizaciones"
      subtitle="Reglas predefinidas que ayudan a no perder leads, mover el trabajo diario y dejar trazabilidad."
    >
      {error && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3 mb-4">{error}</div>}

      {/* Catalog */}
      <div className="grid md:grid-cols-2 gap-3 mb-8">
        {catalog.map(a => {
          const Icon = ICONS[a.id] || Lightning;
          const kind = KIND_LABEL[a.kind] || KIND_LABEL.builtin;
          const lastOk = a.last_run?.outcome === "ok";
          const lastFail = a.last_run?.outcome === "failed";
          return (
            <div
              key={a.id}
              className="bg-white border border-stone-200 rounded-lg p-4 hover:border-stone-900 transition"
              data-testid={`automation-card-${a.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-9 h-9 rounded-md bg-stone-100 flex items-center justify-center text-stone-700">
                  <Icon size={18} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-medium truncate">{a.title}</div>
                    <Chip size="xs" tone={kind.tone}>{kind.label}</Chip>
                  </div>
                  <div className="text-xs text-stone-500 mb-2">{a.description}</div>
                  <div className="flex items-center gap-2 text-[11px] text-stone-500">
                    <span className="font-mono text-stone-400 truncate" title={a.trigger}>
                      {a.trigger}
                    </span>
                    {a.last_run ? (
                      <Chip size="xs" tone={lastOk ? "emerald" : lastFail ? "red" : "stone"}>
                        {lastOk
                          ? <><CheckCircle size={10} weight="bold" /> última {relativeActivity(a.last_run.timestamp)}</>
                          : lastFail
                            ? <><WarningCircle size={10} weight="bold" /> falló {relativeActivity(a.last_run.timestamp)}</>
                            : "—"}
                      </Chip>
                    ) : (
                      <span className="text-stone-400">sin ejecuciones</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {catalog.length === 0 && !error && <div className="text-stone-400 text-sm">Cargando…</div>}
      </div>

      {/* Runs */}
      <Section title="Historial" subtitle="Últimas 50 ejecuciones registradas en el activity log.">
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid="automations-runs">
          {runs.length === 0 && (
            <div className="text-sm text-stone-400 p-4 italic">Sin ejecuciones todavía.</div>
          )}
          {runs.map(r => {
            const ok = r.action.endsWith(".ok");
            const aid = (r.action.match(/^automation\.([^.]+)\./) || [])[1];
            const Icon = ICONS[aid] || Lightning;
            const meta = r.meta || {};
            return (
              <button
                key={r.activity_id}
                type="button"
                onClick={() => {
                  if (r.entity_type === "lead" && r.entity_id) navigate(`/leads/${r.entity_id}`);
                  else if (r.entity_type === "villa" && r.entity_id) navigate(`/villas?focus=${r.entity_id}`);
                }}
                className="w-full text-left flex items-center gap-3 px-4 py-2.5 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 transition"
                data-testid={`automation-run-${r.activity_id}`}
              >
                <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                  ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                }`}>
                  {ok ? <CheckCircle size={12} weight="bold" /> : <WarningCircle size={12} weight="bold" />}
                </span>
                <Icon size={14} className="text-stone-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">
                    {meta.task_title || meta.lead_name || meta.suggested_action || aid || "—"}
                  </div>
                  {!ok && meta.error && (
                    <div className="text-[11px] text-red-600 truncate" title={meta.error}>
                      {meta.error}
                    </div>
                  )}
                </div>
                <div className="shrink-0 text-[11px] text-stone-500 tabular-nums">
                  {fmtDateTime(r.timestamp)}
                  {meta.duration_ms != null && (
                    <span className="text-stone-400"> · {meta.duration_ms}ms</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Section>
    </Section>
  );
}
